import { Component, OnInit, Input, Output, ViewChild, EventEmitter, TemplateRef } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { DatatableComponent } from '@swimlane/ngx-datatable';
import { AlertAndNotificationsService } from 'src/app/core/services/alert-and-notifications.service';
import { JobMatchService } from 'src/app/core/services/job-match.service';
import { AuthService } from 'src/app/core/services/auth.service';
import { BsModalService, BsModalRef } from 'ngx-bootstrap';
import { AngularModalService } from 'src/app/core/services/modal.service';
import { FourDotFiveTimeStampFormatPipe } from 'src/app/core/pipes/four-dot-five-time-stamp-format';

@Component({
  selector: 'app-email-client-review',
  templateUrl: './email-client-review.component.html',
  styleUrls: ['./email-client-review.component.css']
})
export class EmailClientReviewComponent implements OnInit {
  
  @ViewChild('serialNumberTemplate', {static: true}) serialNumberTemplateRef: TemplateRef<any>;
  @ViewChild('candidateNameTemplate', {static: true}) candidateNameTemplateRef: TemplateRef<any>;
  @ViewChild('sentDateTimeTemplate', {static: true}) sentDateTimeTemplateRef: TemplateRef<any>;
  @ViewChild('noDaysTemplate', {static: true}) noDaysTemplateRef: TemplateRef<any>;
  @ViewChild('stepAndStateTemplate', {static: true}) stepAndStateTemplateRef: TemplateRef<any>;
  @ViewChild('clientReviewRecipientsTemplate', {static: true}) clientReviewRecipientsTemplateRef: TemplateRef<any>;
  @ViewChild('clientReviewInformationSentTemplate', {static: true}) clientReviewInformationSentTemplateRef: TemplateRef<any>;
  @ViewChild('responseReceivedTemplate', {static: true}) responseReceivedTemplateRef: TemplateRef<any>;
  @ViewChild('noteTemplate', {static: true}) noteTemplateRef: TemplateRef<any>;
  @ViewChild('emailDetailsModalTemplate', {static: true}) emailDetailsModalTemplateRef: TemplateRef<any>;
  @ViewChild(DatatableComponent, {static: true}) clientReviewTable: DatatableComponent;

  @Input() context; // 'candidateJobMatchCard', 'requisitionCard', 'candidateCard'
  @Input() clientReviewDetails;
  @Input() companyId;
  @Input() userId;
  @Input() candidateId;
  @Input() jobId;
  @Input() jobMatchId;
  @Output() cancelCallback = new EventEmitter<any>();
  @Output() saveCallback = new EventEmitter<any>();
  

  reviewsList: any = [];
  clientReviewTableColumnHeading: any = [];
  isLoading: boolean = true;
  emailDetailsModal: BsModalRef;
  currentEmailDetails: any = null;
  modifiedRowIds: Set<any> = new Set();
  
  constructor(
    private alertsAndNotificationsService: AlertAndNotificationsService,
    private jobMatchService: JobMatchService,
    private authService: AuthService,
    private bsModalService: BsModalService,
    private modalService: AngularModalService,
    private fourDotFiveTimeStampFormatPipe: FourDotFiveTimeStampFormatPipe
  ) { }

  ngOnInit() {
    this.companyId = !this.companyId ? this.authService.getUserDetails().company.companyId : this.companyId;
    this.userId = !this.userId ? this.authService.getUserDetails().id : this.userId;
    this.modifiedRowIds = new Set();
    this.getClientForReviewData(() => {
      this.setTableColumnsBasedOnContext();
    });
  }

  setTableColumnsBasedOnContext() {
    // Always start with Serial #
    this.clientReviewTableColumnHeading = [
      { prop: 'serialNumber', name: 'Serial #', width: 100, sortable: true, draggable: false }
    ];

    // Add context-specific columns
    if (this.context === 'candidateJobMatchCard') {
      this.clientReviewTableColumnHeading.push(
        { prop: 'sentDateTime', name: 'Sent Date Time', cellTemplate: this.sentDateTimeTemplateRef, sortable: false, draggable: false },
        { prop: 'noDays', name: 'No of Days', cellTemplate: this.noDaysTemplateRef, sortable: false, draggable: false },
        { prop: 'stepAndState', name: 'Step and State', cellTemplate: this.stepAndStateTemplateRef, sortable: false, draggable: false },
        { prop: 'recipients', name: 'Recipients', cellTemplate: this.clientReviewRecipientsTemplateRef, sortable: false, draggable: false }
      );
    } else if (this.context === 'requisitionCard') {
      this.clientReviewTableColumnHeading.push(
        { prop: 'candidateName', name: 'Candidate Name', cellTemplate: this.candidateNameTemplateRef, sortable: false, draggable: false },
        { prop: 'sentDateTime', name: 'Sent Date Time', cellTemplate: this.sentDateTimeTemplateRef, sortable: false, draggable: false },
        { prop: 'noDays', name: 'No of Days', cellTemplate: this.noDaysTemplateRef, sortable: false, draggable: false },
        { prop: 'recipients', name: 'Recipients', cellTemplate: this.clientReviewRecipientsTemplateRef, sortable: false, draggable: false }
      );
    } else if (this.context === 'candidateCard') {
      this.clientReviewTableColumnHeading.push(
        { prop: 'reqId', name: 'Req ID', sortable: true, draggable: false },
        { prop: 'reqName', name: 'Req Name', sortable: true, draggable: false },
        { prop: 'clientOrBuName', name: 'Client/BU Name', sortable: true, draggable: false },
        { prop: 'sentDateTime', name: 'Sent Date Time', cellTemplate: this.sentDateTimeTemplateRef, sortable: false, draggable: false },
        { prop: 'noDays', name: 'No of Days', cellTemplate: this.noDaysTemplateRef, sortable: false, draggable: false },
        { prop: 'recipients', name: 'Recipients', cellTemplate: this.clientReviewRecipientsTemplateRef, sortable: false, draggable: false }
      );
    }

    // Add common columns for all contexts
    this.clientReviewTableColumnHeading.push(
      { prop: 'informationSent', name: 'Information Sent', cellTemplate: this.clientReviewInformationSentTemplateRef, sortable: false, draggable: false },
      { prop: 'responseReceived', name: 'Response Received', cellTemplate: this.responseReceivedTemplateRef, width: 125, sortable: false, draggable: false },
      { prop: 'responseNote', name: 'Note', width: 250, cellTemplate: this.noteTemplateRef, sortable: false, draggable: false }
    );
  }

  getClientForReviewData(successCallback) {
    this.isLoading = true;
    this.jobMatchService.getClientForReviewData(this.companyId, this.userId, this.candidateId, this.jobId, this.jobMatchId, (data) => {
      this.reviewsList = this.processClientReviewData(data);
      // Sort based on context
      if (this.context === 'requisitionCard') {
        // Sort by candidateName first, then by sentDateTime (most recent first)
        this.reviewsList.sort((a, b) => {
          const nameComparison = a.candidateName.localeCompare(b.candidateName);
          if (nameComparison !== 0) {
            return nameComparison;
          }
          return new Date(b.sentDateTimeRaw).getTime() - new Date(a.sentDateTimeRaw).getTime();
        });
      } else {
        // Sort by sentDateTimeRaw in descending order (most recent first) for other contexts
        this.reviewsList.sort((a, b) => new Date(b.sentDateTimeRaw).getTime() - new Date(a.sentDateTimeRaw).getTime());
      }
      if (successCallback) {
        successCallback();
      }
      this.isLoading = false;
    }, (error) => {
      this.alertsAndNotificationsService.showBannerMessage('Unable to fetch client review details. Please try again later.', 'error');
      this.isLoading = false;
    });
  }

  processClientReviewData(data: any[]): any[] {
    if (!data || !Array.isArray(data)) {
      return [];
    }

    return data.map((item, index) => {
      // Sort clientReviewIncludeFields by displayOrder if they exist
      let sortedFields = item.emailDetails && item.emailDetails.clientReviewIncludeFields 
        ? [...item.emailDetails.clientReviewIncludeFields].sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0))
        : [];

      const processedItem = {
        ...item,
        serialNumber: index + 1,
        candidateName: `${item.candidateFirstName} ${item.candidateLastName}`,
        sentDateTime: this.formatDate(item.sentDateTime),
        sentDateTimeRaw: item.sentDateTime,
        noDays: this.calculateDaysSinceSent(item.sentDateTime),
        stepAndState: {
          currentStep: this.formatStateOrStepText(item.currentStep) || '--',
          currentState: this.formatStateOrStepText(item.currentState) || '--'
        },
        recipients: item.recipients && Array.isArray(item.recipients) ? item.recipients : [],
        hasClientReviewFields: item.emailDetails ? item.emailDetails.clientReviewIncludeFields && item.emailDetails.clientReviewIncludeFields.length > 0 : false,
        informationSent: item.emailDetails ? item.emailDetails.clientReviewIncludeFields && item.emailDetails.clientReviewIncludeFields.length > 0 ? 'View' : 'Follow Up message' : 'Follow Up message',
        responseReceived: item.responseReceived || false,
        responseNote: item.responseNote || '',
        emailDetails: {
          ...item.emailDetails,
          clientReviewIncludeFields: sortedFields
        },
        reqId: item.reqId || '--',
        reqName: item.reqName || '--',
        clientOrBuName: item.clientOrBuName || '--'
      };

      return processedItem;
    });
  }

  calculateDaysSinceSent(sentDateTime: any): number {
    if (!sentDateTime) {
      return 0;
    }
    const sentDate = this.convertToJavaScriptDate(sentDateTime);
    if (!sentDate) {
      return 0;
    }
    const today = new Date();
    const diffTime = Math.abs(today.getTime() - sentDate.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  }

  convertToJavaScriptDate(dateObj: any): Date | null {
    if (!dateObj) {
      return null;
    }
    
    if (dateObj instanceof Date) {
      return dateObj;
    }
    
    if (typeof dateObj === 'string') {
      return new Date(dateObj);
    }
    
    if (dateObj.millis) {
      return new Date(dateObj.millis);
    }
    
    if (dateObj.year && dateObj.monthOfYear && dateObj.dayOfMonth) {
      return new Date(dateObj.year, dateObj.monthOfYear - 1, dateObj.dayOfMonth, 
                      dateObj.hourOfDay || 0, dateObj.minuteOfHour || 0, dateObj.secondOfMinute || 0);
    }
    
    return null;
  }

  formatDate(date: any): string {
    if (!date) {
      return '--';
    }
    
    // Convert to JavaScript Date if needed
    const dateObj = this.convertToJavaScriptDate(date);
    if (!dateObj) {
      return '--';
    }
    
    return this.fourDotFiveTimeStampFormatPipe.transform(dateObj);
  }

  onResponseCheckboxChange(row: any) {
    this.modifiedRowIds.add(row.id);
    this.onSaveResponseAndNote(row);
  }

  onNoteChange(row: any) {
    this.modifiedRowIds.add(row.id);
  }

  onSaveResponseAndNote(row: any) {
    const payload = {
      id: row.id,
      responseReceived: row.responseReceived,
      responseNote: row.responseNote,
      responseUpdatedDateTime: new Date().toISOString()
    };
  }

  formatStateOrStepText(text: string): string {
    if (!text) {
      return text;
    }
    return text
      .toLowerCase()
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  }

  viewEmailDetails(row: any) {
    this.currentEmailDetails = {
      subject: row.emailDetails ? row.emailDetails.subject || 'N/A' : 'N?/A',
      body: row.emailDetails ? row.emailDetails.body || 'No body content' : 'No body content',
      fields: row.emailDetails ? row.emailDetails.clientReviewIncludeFields || [] : [],
      sentDateTime: row.sentDateTime
    };
    this.emailDetailsModal = this.bsModalService.show(this.emailDetailsModalTemplateRef, { class: 'modal-lg custom-ngx-modal' });
  }

  closeEmailDetailsModal() {
    if (this.emailDetailsModal) {
      this.emailDetailsModal.hide();
    }
    this.modalService.addModalOpenClassToBodyIfAnyModalIsOpen();
  }

  closeModal() {
    this.cancelCallback.emit();
  }

  resetTableSorting() {
    if (this.clientReviewTable) {
      this.clientReviewTable.sorts = [];
    }
  }

  saveClientReview() {
    let clientReviewData = [];
    this.reviewsList
      .filter(review => this.modifiedRowIds.has(review.id))
      .forEach(review => {
        clientReviewData.push({
          id: review.id,
          responseReceived: review.responseReceived,
          responseNote: review.responseNote,
        });
      });
    this.jobMatchService.saveClientReviewResponse(this.companyId, this.userId, clientReviewData, (response) => {
      this.saveCallback.emit();
      this.alertsAndNotificationsService.showBannerMessage(response.message, 'success');
    }, (error) => {
      this.alertsAndNotificationsService.showBannerMessage(error.message, 'error');
    });
  }

}
